import type { PuzzleState, Session } from '../domain/puzzle/PuzzleSessionController';

export function solveTime(ms: number) {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return seconds < 60
    ? `${seconds}s`
    : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
}

/** One calm line of solve feedback: how it went, how long it took, and the streak. */
export function solveSummary(s: Session, streak = 0) {
  const time = solveTime((s.completedAt ?? s.startedAt) - s.startedAt);
  if (s.revealed) return `Solution revealed · ${time}`;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  if (!s.mistakes.length && !s.hintsUsed)
    return `Clean solve · ${time}${streak >= 2 ? ` · streak ${streak}` : ''}`;
  return [
    'Solved',
    s.mistakes.length && plural(s.mistakes.length, 'mistake'),
    s.hintsUsed && plural(s.hintsUsed, 'hint'),
    time,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function TurnPrompt({
  state: { status, session: s },
  ready,
  loadingLabel,
  streak = 0,
}: {
  state: PuzzleState;
  ready: boolean;
  loadingLabel: string;
  streak?: number;
}) {
  // The setup move (R5) keeps the same title, so the prompt doesn't flicker.
  const userTurn =
    status === 'user-turn' || status === 'continuation-user-turn' || status === 'preparing';
  const mistake = status === 'incorrect' || status.startsWith('mistake-');
  const replying = ['correct-step', 'auto-reply-delay', 'auto-reply-animating'].includes(status);
  const solved = s.solved && !s.continuation;
  const solver = s.puzzle.solverColor === 'w' ? 'White' : 'Black';
  // Never reveal the solution length: it tells the solver how deep to calculate.
  const title = !ready
    ? 'Preparing your practice room'
    : userTurn
      ? `${solver} to move`
      : mistake
        ? 'Explore this move'
        : replying
          ? 'Good move.'
          : s.continuation
            ? status === 'game-over'
              ? s.result === 'checkmate'
                ? s.fen.split(' ')[1] === s.puzzle.solverColor
                  ? 'Checkmate.'
                  : 'A fine finish.'
                : 'Position finished.'
              : s.continuation.chosen === 'finish-mate'
                ? 'Finish the mate.'
                : 'Make it your own.'
            : solved
              ? 'Tactic found.'
              : 'Puzzle practice';
  const feedback =
    ready &&
    s.message !== title &&
    s.message !== 'Find the best continuation.' &&
    !(replying && s.message === 'Good move. Keep going.')
      ? s.message
      : '';
  const tone = ready ? (mistake ? 'mistake' : solved || replying ? 'good' : '') : '';

  return (
    <div
      className={`panel-copy turn-prompt ${tone ? `turn-prompt--${tone}` : ''}`}
      data-testid="turn-prompt"
      aria-live="polite"
      aria-atomic="true"
    >
      <h1>
        {ready &&
          (solved ? (
            <svg className="turn-tick" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                pathLength="1"
                d="m5 12 4 4L19 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              />
            </svg>
          ) : (
            <span className={`turn-disc turn-disc--${s.puzzle.solverColor}`} aria-hidden="true" />
          ))}
        {title}
      </h1>
      {!ready ? (
        <>
          <p>{loadingLabel}</p>
          <a href="/?source=fixtures">Open sample practice</a>
        </>
      ) : (
        <>
          {solved && (
            <p className="turn-feedback solve-summary" data-testid="solve-summary">
              {solveSummary(s, streak)}
            </p>
          )}
          {feedback && <p className="turn-feedback">{feedback}</p>}
          {s.refutation && (
            <span className="line-progress">
              Engine line
              {s.refutation.index >= s.refutation.moves.length ? ' · Retry to solve' : ''}
            </span>
          )}
        </>
      )}
    </div>
  );
}
